import { SEO } from '@/components/SEO'

export default function PrivacyPolicy() {
  return (
    <>
      <SEO
        title="Privacy Policy - WithWords"
        description="Learn about how WithWords collects, uses, and protects your personal information."
      />
      
      <div className="container py-8 max-w-4xl mx-auto px-4">
        <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground mb-8">Last Updated: April 8, 2025</p>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Introduction</h2>
          <p className="mb-4">
            AI Labs Group LLC ("we," "our," or "us"), a company based in the state of Georgia, is committed to protecting your privacy.
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services.
          </p>
          <p className="mb-4">
            Please read this Privacy Policy carefully. By accessing or using our services, you acknowledge that you have read, understood, and agree to be bound by all the terms of this Privacy Policy.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Information We Collect</h2>
          <p className="mb-4">We may collect several types of information from and about users of our services, including:</p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>
              <strong>Personal Information:</strong> This includes information that can be used to identify you, such as your name, email address, postal address, phone number, and other identifiers.
            </li>
            <li>
              <strong>Account Information:</strong> Information you provide when creating an account, such as username and password.
            </li>
            <li>
              <strong>Payment Information:</strong> If you make purchases through our services, we collect payment information, including credit card numbers, billing addresses, and other financial information.
            </li>
            <li>
              <strong>Usage Data:</strong> Information about how you use our services, including your browsing actions, search queries, and other interactions with our website.
            </li>
            <li>
              <strong>Device Information:</strong> Information about the device you use to access our services, including hardware model, operating system, unique device identifiers, and mobile network information.
            </li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">How We Use Your Information</h2>
          <p className="mb-4">We may use the information we collect for various purposes, including to:</p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Provide, maintain, and improve our services</li>
            <li>Process transactions and send related information</li>
            <li>Send administrative information, such as updates, security alerts, and support messages</li>
            <li>Respond to your comments, questions, and requests</li>
            <li>Personalize your experience and deliver content relevant to your interests</li>
            <li>Monitor and analyze trends, usage, and activities in connection with our services</li>
            <li>Detect, prevent, and address technical issues, fraud, and other illegal activities</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Disclosure of Your Information</h2>
          <p className="mb-4">We may disclose your personal information in the following circumstances:</p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>
              <strong>Service Providers:</strong> We may share your information with third-party vendors, service providers, and other business partners who perform services on our behalf.
            </li>
            <li>
              <strong>Business Transfers:</strong> If we are involved in a merger, acquisition, or sale of all or a portion of our assets, your information may be transferred as part of that transaction.
            </li>
            <li>
              <strong>Legal Requirements:</strong> We may disclose your information if required to do so by law or in response to valid requests by public authorities.
            </li>
            <li>
              <strong>Protection of Rights:</strong> We may disclose your information to protect our rights, privacy, safety, or property, and that of our users or others.
            </li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Cookies and Tracking Technologies</h2>
          <p className="mb-4">
            We use cookies and similar tracking technologies to track activity on our services and store certain information.
            Cookies are files with a small amount of data that may include an anonymous unique identifier.
            You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
          </p>
          <p className="mb-4">
            We use cookies for various purposes, including to:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Keep you signed in</li>
            <li>Understand how you use our services</li>
            <li>Remember your preferences</li>
            <li>Improve your experience</li>
            <li>Measure the effectiveness of our marketing campaigns</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Data Security</h2>
          <p className="mb-4">
            We implement appropriate technical and organizational measures to protect the security of your personal information.
            However, please be aware that no method of transmission over the Internet or method of electronic storage is 100% secure.
            While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Your Data Protection Rights</h2>
          <p className="mb-4">Depending on your location, you may have certain rights regarding your personal information, including:</p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>The right to access your personal information</li>
            <li>The right to rectify inaccurate personal information</li>
            <li>The right to request the deletion of your personal information</li>
            <li>The right to restrict the processing of your personal information</li>
            <li>The right to data portability</li>
            <li>The right to object to the processing of your personal information</li>
          </ul>
          <p className="mb-4">
            To exercise any of these rights, please contact us using the contact information provided below.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Children's Privacy</h2>
          <p className="mb-4">
            Our services are not intended for individuals under the age of 13. We do not knowingly collect personal information from children under 13.
            If you are a parent or guardian and you believe your child has provided us with personal information, please contact us, and we will take steps to delete such information.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Changes to This Privacy Policy</h2>
          <p className="mb-4">
            We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date at the top of this Privacy Policy.
            You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Contact Us</h2>
          <p className="mb-4">
            If you have any questions about this Privacy Policy, please contact us at:
          </p>
          <p className="text-muted-foreground">
            AI Labs Group LLC<br />
            Email: privacy@withwords.app
          </p>
        </section>
      </div>
    </>
  )
}